//lib
import { pdf } from '@react-pdf/renderer';
import { useNavigate, useParams } from 'react-router-dom';
import React, { useState, useRef, useContext, useEffect, useMemo } from 'react';
import SelectField from '../../../../components/form-fields/select-field/select-field';

//src
import ChartContext from '../../../../context/ChartContext';
import TimelinesPDF from '../../pdf/timeline-pdf';
import TimelinesFFD from '../../pdf/timeline-pdf-ffd';
import { templatesCollection } from '../../../../services/firebase';
import SaveTemplateModal from '../../template/save-template/save-template-modal';
import { Button } from '../../../../components/button/button';
import { HeaderActionsStyled } from './header-actions.style';
import { useCompanyDetails } from '../../../../hooks/useCompanyDetails';
import useContactName from '../../../../hooks/useContactName';
import EstimatePdf from '../../pdf/estimate-pdf';
import EstimateFFD from '../../pdf/estimate-pdf-ffd';
import EstimateFFP from '../../pdf/estimate-pdf-ffp';
import EstimateMaterials from '../../pdf/estimate-materials';
import { SaveIcon } from '../../../../utils/svg-components';
import TableActionMenu from '../../../../components/table-action-menu/table-action-menu';
import { Typography } from '@mui/material';
import { ESTIMATE_OPTIONS, getTimelinesMenuOptions } from './header-actions.utils';
import { PLANNER_TYPES } from '../../planner.constants';
import { useClickOutside } from './header-actions.hooks';
import { getProjectStartAndEndDate } from '../../../../utils/utils';

const HeaderActions = ({
  isTimelinesInValid,
  projectData,
  milestones,
  resources,
  materials,
  handleSave,
  handleStatusChange,
  discountObj,
  contact,
  isGeneralUser,
  project,
  onPlannerTypeChange,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const { exportGanttChart } = useContext(ChartContext);
  const { companyDetails } = useCompanyDetails(projectData?.client);
  const plannerServiceValue = useMemo(
    () => projectData?.plannerServiceValue,
    [projectData?.plannerServiceValue],
  );
  const [showPopup, setShowPopup] = useState(false);

  useClickOutside(dropdownRef);

  const { fullName: contactName } = useContactName(contact);

  const TYPE_TNM = 'Time + Materials';
  const TYPE_FFD = 'Fixed Fee Deliverable';
  const TYPE_FFP = 'Fixed Fee Project';
  const TYPE_M = 'Materials';

  const exportEstimate = async (forceFFP) => {
    // only enforce task-level checks on Time & Materials
    const type = projectData.plannerType;
    const isTnM = type === TYPE_TNM;
    const isFFD = type === TYPE_FFD;
    const isFFP = type === TYPE_FFP;
    const isM = type === TYPE_M;

    // MATERIALS VALIDATION
    if (isM) {
      const hasValidMaterials =
        Array.isArray(materials) &&
        materials.some((mat) => mat.description && mat.description.trim() !== '');
      if (!hasValidMaterials) {
        alert(
          'Unable to export estimate PDF. Please add at least one material description to proceed.',
        );
        return;
      }
    }

    if (type !== TYPE_M) {
      const hasValidDeliverables = project.milestones.some((deliverable) => {
        // every deliverable needs a non-empty name
        if (!deliverable.name.trim()) return false;

        // for T&M, also require at least one enabled resource on one task
        if (isTnM) {
          return deliverable.tasks.some(
            (task) => task.description.trim() !== '' && task.resources.some((r) => r.isEnabled),
          );
        }

        // for fixed-fee types, name alone suffices
        return true;
      });

      if (!hasValidDeliverables) {
        alert(
          'Unable to export estimate PDF. Please add a budget to your project plan to proceed.',
        );
        return;
      }
    }

    let EstimateComponent;

    if (forceFFP) {
      EstimateComponent = EstimateFFD;
    } else if (isFFD) {
      EstimateComponent = EstimateFFD;
    } else if (isFFP) {
      EstimateComponent = EstimateFFP;
    } else if (isM) {
      EstimateComponent = EstimateMaterials;
    } else {
      EstimateComponent = EstimatePdf;
    }

    // 3) render + open
    const pdfData = (
      <EstimateComponent
        projectData={project}
        materials={materials}
        discountObj={discountObj}
        companyDetails={companyDetails}
        contactName={contactName}
      />
    );
    pdf(pdfData)
      .toBlob()
      .then((blob) => window.open(URL.createObjectURL(blob), '_blank'));
  };

  const exportTimeline = (forceSimple) => {
    const projectDateRange = getProjectStartAndEndDate(milestones);

    const type = projectData.plannerType;
    const isTnM = type === TYPE_TNM;
    const isFFD = type === TYPE_FFD;

    // first, ensure no task is missing dates
    const tasksMissingDates = milestones.some((m) =>
      m.tasks.some((t) => t.description.trim() !== '' && (!t.startDate || !t.endDate)),
    );
    if (tasksMissingDates) {
      alert(
        'Unable to export. Please ensure every task has both a start and an end date before exporting the timeline.',
      );
      return;
    }

    // every type needs at least one named milestone
    const hasValidMilestones = milestones.some((m) => m.name.trim() !== '');
    // only T&M also needs at least one task description
    const hasValidTasks = isTnM
      ? milestones.some((m) => m.tasks.some((t) => t.description.trim() !== ''))
      : true;

    if (!hasValidMilestones || !hasValidTasks) {
      alert(
        'Unable to display timeline PDF. Please ensure you have at least one deliverable name' +
          (isTnM ? ' and one task description.' : '.'),
      );
      return;
    }

    // Create a PDF document using the TemplatePage component
    const TimelineComponent = forceSimple || isFFD ? TimelinesFFD : TimelinesPDF;

    const pdfData = (
      <TimelineComponent
        id={id}
        materials={materials}
        milestones={milestones}
        projectData={projectData}
        projectDateRange={projectDateRange}
        companyDetails={companyDetails}
        contactName={contactName}
      />
    );

    // Generate a Blob containing the PDF data
    pdf(pdfData)
      .toBlob()
      .then((blob) => {
        // Create a URL for the Blob
        const pdfUrl = URL.createObjectURL(blob);

        // Open the PDF in a new tab
        window.open(pdfUrl, '_blank');
      });
  };

  const exportGantt = () => {
    if (exportGanttChart) exportGanttChart();
  };

  // inside HeaderActions.js (or wherever you define handleSaveTemplate)
  const handleSaveTemplate = (templateName, templateDescription) => {
    // pull out anything you want to handle specially, and keep the rest
    const { isNew = false, ...restOfProjectData } = projectData;

    const newTemplate = {
      name: templateName,
      description: templateDescription,

      // everything else from projectData
      data: {
        ...restOfProjectData,
      },

      // your deliverables and materials arrays
      deliverables: [...milestones],
      materials: [...materials],

      // if you want to store root-level resources & schedule too
      resources: [...resources],

      // keep the isNew flag if you need it on re-apply
      isNew,
    };

    templatesCollection
      .doc(templateName)
      .set(newTemplate)
      .then(() => {
        console.log('Template successfully saved with name:', templateName);
      })
      .catch((error) => {
        console.error('Error saving template:', error);
      });
  };

  const handleSaveAsTemplate = () => setShowPopup(true);

  const handleMenuCLick = (e) => {
    if (isTimelinesInValid)
      window.alert(
        'Please add at least one material OR specify one deliverable and task description to access the menu options.',
      );
  };

  // useEffect(() => {
  //   const hasBudget =
  //     Array.isArray(project?.milestones) &&
  //     project.milestones.some((milestone) => milestone?.budget > 0);

  //   const hasPlannerServiceValue = !!plannerServiceValue;

  //   if (
  //     (hasBudget || hasPlannerServiceValue) &&
  //     projectData.status !== "Draft"
  //   ) {
  //     handleStatusChange("Draft");
  //   }
  // }, [
  //   project?.milestones,
  //   projectData.status,
  //   handleStatusChange,
  //   plannerServiceValue,
  // ]);

  const hasMilestones = Array.isArray(milestones) && milestones.some((m) => m.name.trim() !== '');

  const actionsOptionList = getTimelinesMenuOptions({
    isGeneralUser,
    exportEstimate,
    exportTimeline,
    exportSimpleEstimate: () => exportEstimate(true),
    exportSimpleTimeline: () => exportTimeline(true),
    exportGantt,
    handleSaveAsTemplate,
    plannerType: projectData.plannerType,
  });
  return (
    <HeaderActionsStyled isGeneralUser={isGeneralUser}>
      <div className="info-buttons">
        <div className="est-status">
          <Typography variant="body2" className="label">
            Estimate Status:
          </Typography>
          <div className="select">
            <SelectField
              placeholder="Please Select"
              value={projectData.status}
              disabled={!hasMilestones}
              onChange={(e) => handleStatusChange(e.target.value)}
              options={ESTIMATE_OPTIONS}
            />
          </div>
        </div>
        <div className="est-status">
          <Typography variant="body2" className="label">
            Planner Type:
          </Typography>
          <div className="select">
            <SelectField
              placeholder="Please Select"
              value={projectData.plannerType}
              disabled={!hasMilestones}
              options={PLANNER_TYPES}
              onChange={(e) => onPlannerTypeChange(e.target.value)}
            />
          </div>
        </div>

        <div className="action-btns">
          {!isGeneralUser && (
            <Button
              // disabled={isGeneralUser}
              size="large"
              onClick={() => handleSave()}
              variant="filled"
              startIcon={<SaveIcon />}
              color={'tertiary'}
            >
              Save Plan
            </Button>
          )}
          {!isGeneralUser && (
            <div onClick={handleMenuCLick} className="menu-wrapper">
              <div style={{ pointerEvents: isTimelinesInValid ? 'none' : '' }}>
                <TableActionMenu noBorder width={180} options={actionsOptionList} />
              </div>
            </div>
          )}
        </div>
      </div>

      {showPopup && (
        <SaveTemplateModal
          show={showPopup}
          onClose={() => setShowPopup(false)}
          onSave={handleSaveTemplate}
        />
      )}
    </HeaderActionsStyled>
  );
};

export default HeaderActions;
